import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useSnackbar } from 'notistack';
import tenantEmailSettingsService, {
  TenantEmailRemitente,
  TenantEmailSettingsStatus,
} from '../../services/tenantEmailSettingsService';
import {
  detectProviderFromEmail,
  detectProviderFromSmtpServer,
  resolveSmtpForEmail,
  SMTP_PRESETS,
  type EmailProvider,
} from '../../utils/emailProviderPresets';
import {
  CORREO_TIPOS_DOCUMENTO,
  labelCorreoTipoDocumento,
} from '../../config/correoDocumentoTipos';

interface SmtpForm {
  email: string;
  appPassword: string;
  fromName: string;
  smtpServer: string;
  smtpPort: number;
  enableSsl: boolean;
}

interface RemitenteForm {
  tipoDocumento: string;
  fromEmail: string;
  fromName: string;
}

const emptySmtp: SmtpForm = {
  email: '',
  appPassword: '',
  fromName: '',
  smtpServer: '',
  smtpPort: 587,
  enableSsl: true,
};

const emptyRemitente: RemitenteForm = {
  tipoDocumento: 'FACTURAS',
  fromEmail: '',
  fromName: '',
};

function smtpHelpForProvider(provider: EmailProvider): React.ReactNode {
  if (provider === 'gmail') {
    return (
      <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Para Gmail / Google Workspace necesitás una <strong>contraseña de aplicación</strong>
          (no la contraseña normal de la cuenta).
        </Typography>
        <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
          <li>Activá la verificación en 2 pasos en la cuenta de Google.</li>
          <li>Abrí Contraseñas de aplicaciones.</li>
          <li>Creá una para “Correo” / eOne y pegala acá.</li>
        </Typography>
        <Link
          href="https://myaccount.google.com/apppasswords"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-block', mt: 1 }}
        >
          Crear contraseña de aplicación en Google
        </Link>
      </>
    );
  }

  if (provider === 'outlook') {
    return (
      <>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Para Outlook / Microsoft 365 necesitás una <strong>contraseña de aplicación</strong>
          (con MFA activo), no la contraseña habitual.
        </Typography>
        <Typography variant="body2" component="ol" sx={{ pl: 2, m: 0 }}>
          <li>Entrá a Seguridad de la cuenta Microsoft.</li>
          <li>Activá la verificación en dos pasos si aún no está.</li>
          <li>Creá una contraseña de aplicación y pegala acá.</li>
        </Typography>
        <Link
          href="https://account.microsoft.com/security"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ display: 'inline-block', mt: 1 }}
        >
          Ir a seguridad de Microsoft
        </Link>
      </>
    );
  }

  return (
    <>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Dominio corporativo u otro proveedor: las instrucciones dependen del host de correo.
      </Typography>
      <Typography variant="body2" component="ul" sx={{ pl: 2, m: 0 }}>
        <li>
          Si usan <strong>Google Workspace</strong> con dominio propio: igual que Gmail
          (contraseña de aplicación) y servidor <em>smtp.gmail.com</em>.
        </li>
        <li>
          Si usan <strong>Microsoft 365</strong> con dominio propio: igual que Outlook
          y servidor <em>smtp.office365.com</em>.
        </li>
        <li>
          Si es otro host (cPanel, hosting, etc.): pedí a tu proveedor el servidor SMTP,
          puerto, SSL y si permite autenticación con la contraseña de la casilla.
        </li>
      </Typography>
    </>
  );
}

const CorreoSalida: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingRemitente, setSavingRemitente] = useState(false);
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [confirmingTipo, setConfirmingTipo] = useState<string | null>(null);
  const [codigos, setCodigos] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<TenantEmailSettingsStatus | null>(null);
  const [form, setForm] = useState<SmtpForm>(emptySmtp);
  const [remitenteForm, setRemitenteForm] = useState<RemitenteForm>(emptyRemitente);

  const detectedProvider = useMemo(() => {
    const fromEmail = detectProviderFromEmail(form.email);
    if (fromEmail !== 'custom') return fromEmail;
    return detectProviderFromSmtpServer(form.smtpServer);
  }, [form.email, form.smtpServer]);

  const isKnownProvider = detectedProvider !== 'custom';
  const remitentes = status?.remitentes ?? [];

  const applyStatus = useCallback((data: TenantEmailSettingsStatus) => {
    setStatus(data);
    if (data.configurado) {
      const email = data.username ?? data.fromEmail ?? '';
      const providerFromEmail = detectProviderFromEmail(email);
      const provider =
        providerFromEmail !== 'custom'
          ? providerFromEmail
          : detectProviderFromSmtpServer(data.smtpServer);

      const preset =
        provider === 'gmail'
          ? SMTP_PRESETS.gmail
          : provider === 'outlook'
            ? SMTP_PRESETS.outlook
            : resolveSmtpForEmail(email, {
                smtpServer: data.smtpServer ?? '',
                smtpPort: data.smtpPort ?? 587,
                enableSsl: data.enableSsl ?? true,
              });

      setForm({
        email,
        appPassword: '',
        fromName: data.fromName ?? '',
        smtpServer: preset.smtpServer,
        smtpPort: preset.smtpPort,
        enableSsl: preset.enableSsl,
      });
      setTestEmail((prev) => prev || email);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await tenantEmailSettingsService.getStatus();
      applyStatus(data);
    } catch (e) {
      console.error(e);
      enqueueSnackbar('Error al cargar la configuración de correo', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [applyStatus, enqueueSnackbar]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleEmailChange = (email: string) => {
    const smtp = resolveSmtpForEmail(email, {
      smtpServer: form.smtpServer,
      smtpPort: form.smtpPort,
      enableSsl: form.enableSsl,
    });
    setForm((prev) => ({
      ...prev,
      email,
      smtpServer: smtp.smtpServer,
      smtpPort: smtp.smtpPort,
      enableSsl: smtp.enableSsl,
    }));
  };

  const handleSaveSmtp = async () => {
    const email = form.email.trim();
    if (!email) {
      enqueueSnackbar('Ingrese el correo de la cuenta SMTP', { variant: 'warning' });
      return;
    }
    if (!status?.tieneContrasena && !form.appPassword.trim()) {
      enqueueSnackbar('Ingrese la contraseña de aplicación', { variant: 'warning' });
      return;
    }

    const smtp = resolveSmtpForEmail(email, form);
    if (!smtp.smtpServer.trim()) {
      enqueueSnackbar('Complete el servidor SMTP en la configuración avanzada', { variant: 'warning' });
      return;
    }

    try {
      setSavingSmtp(true);
      const saved = await tenantEmailSettingsService.save({
        smtpServer: smtp.smtpServer.trim(),
        smtpPort: smtp.smtpPort,
        username: email,
        appPassword: form.appPassword.trim() || undefined,
        fromEmail: email,
        fromName: form.fromName.trim() || undefined,
        enableSsl: smtp.enableSsl,
      });
      applyStatus(saved);
      setForm((prev) => ({ ...prev, appPassword: '' }));
      enqueueSnackbar('Cuenta SMTP guardada.', { variant: 'success' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo guardar la configuración SMTP';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleSaveRemitente = async () => {
    const tipo = remitenteForm.tipoDocumento.trim().toUpperCase();
    const fromEmail = remitenteForm.fromEmail.trim();
    if (!tipo) {
      enqueueSnackbar('Seleccione el tipo de documento', { variant: 'warning' });
      return;
    }
    if (!fromEmail) {
      enqueueSnackbar('Ingrese el correo del remitente', { variant: 'warning' });
      return;
    }

    const next: TenantEmailRemitente[] = [
      ...remitentes.filter((r) => r.tipoDocumento.toUpperCase() !== tipo),
      {
        tipoDocumento: tipo,
        fromEmail,
        fromName: remitenteForm.fromName.trim() || undefined,
      },
    ];

    try {
      setSavingRemitente(true);
      const saved = await tenantEmailSettingsService.saveRemitentes({ remitentes: next });
      applyStatus(saved);
      setRemitenteForm(emptyRemitente);
      const pending = saved.remitentes?.find(
        (r) => r.tipoDocumento.toUpperCase() === tipo && r.pendienteConfirmacion
      );
      if (pending) {
        enqueueSnackbar(
          `Enviamos un código a ${fromEmail}. Ingresalo abajo para activar el remitente.`,
          { variant: 'info' }
        );
      } else {
        enqueueSnackbar(`Remitente de ${labelCorreoTipoDocumento(tipo)} guardado.`, { variant: 'success' });
      }
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo guardar el remitente';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSavingRemitente(false);
    }
  };

  const handleConfirm = async (tipo: string) => {
    const codigo = (codigos[tipo] ?? '').trim();
    if (!codigo) {
      enqueueSnackbar('Ingrese el código recibido por mail', { variant: 'warning' });
      return;
    }
    try {
      setConfirmingTipo(tipo);
      const saved = await tenantEmailSettingsService.confirmRemitente(tipo, codigo);
      applyStatus(saved);
      setCodigos((prev) => {
        const next = { ...prev };
        delete next[tipo];
        return next;
      });
      enqueueSnackbar(`Remitente de ${labelCorreoTipoDocumento(tipo)} confirmado.`, { variant: 'success' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo confirmar el código';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setConfirmingTipo(null);
    }
  };

  const handleResend = async (tipo: string) => {
    try {
      setConfirmingTipo(tipo);
      const saved = await tenantEmailSettingsService.resendCodigo(tipo);
      applyStatus(saved);
      enqueueSnackbar('Código reenviado. Revisá el correo del remitente.', { variant: 'info' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo reenviar el código';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setConfirmingTipo(null);
    }
  };

  const handleEditRemitente = (item: TenantEmailRemitente) => {
    setRemitenteForm({
      tipoDocumento: item.tipoDocumento,
      fromEmail: item.fromEmail,
      fromName: item.fromName ?? '',
    });
  };

  const handleRemoveRemitente = async (tipo: string) => {
    if (!window.confirm(`¿Eliminar el remitente de ${labelCorreoTipoDocumento(tipo)}?`)) return;
    try {
      setSavingRemitente(true);
      const saved = await tenantEmailSettingsService.removeRemitente(tipo);
      applyStatus(saved);
      enqueueSnackbar('Remitente eliminado', { variant: 'info' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo eliminar el remitente';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSavingRemitente(false);
    }
  };

  const handleRemoveAll = async () => {
    if (!window.confirm('¿Eliminar la configuración de correo de esta empresa (SMTP y remitentes)?')) return;
    try {
      setSavingSmtp(true);
      await tenantEmailSettingsService.remove();
      setStatus({ configurado: false, tieneContrasena: false, remitentes: [] });
      setForm(emptySmtp);
      setRemitenteForm(emptyRemitente);
      setCodigos({});
      setTestEmail('');
      enqueueSnackbar('Configuración eliminada', { variant: 'info' });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo eliminar la configuración';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTestSmtp = async () => {
    const destination = (testEmail.trim() || form.email.trim());
    if (!destination) {
      enqueueSnackbar('Indicá un correo de destino para la prueba', { variant: 'warning' });
      return;
    }
    if (!status?.configurado) {
      enqueueSnackbar('Guardá primero la cuenta SMTP y después probá el envío', { variant: 'warning' });
      return;
    }
    try {
      setTestingSmtp(true);
      const result = await tenantEmailSettingsService.testSmtp(destination);
      enqueueSnackbar(result.message || `Correo de prueba enviado a ${destination}`, {
        variant: 'success',
      });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'No se pudo enviar el correo de prueba';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setTestingSmtp(false);
    }
  };

  const busy = savingSmtp || savingRemitente || testingSmtp || confirmingTipo !== null;
  const helpTitle =
    detectedProvider === 'gmail'
      ? 'Cómo obtener la contraseña (Gmail / Google)'
      : detectedProvider === 'outlook'
        ? 'Cómo obtener la contraseña (Outlook / Microsoft)'
        : 'Cómo obtener la contraseña (otro dominio / host)';

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', mb: 1 }}>
            Envio de Correos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            La cuenta SMTP autentica el envío. Los remitentes por tipo (si son otro correo) se confirman
            con un código enviado por eOne a esa casilla.
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4 }}>
              <CircularProgress size={24} />
              <Typography color="text.secondary">Cargando…</Typography>
            </Box>
          ) : (
            <Stack spacing={3} sx={{ maxWidth: 720 }}>
              {status?.configurado && status.contrasenaLegible === false && (
                <Alert severity="error" variant="outlined">
                  La cuenta SMTP está guardada, pero la contraseña no se puede leer. Volvé a ingresarla
                  y guardá la cuenta para poder enviar mails.
                </Alert>
              )}
              {status?.configurado && status.contrasenaLegible !== false && (
                <Alert severity="success" variant="outlined">
                  SMTP activo con la cuenta <strong>{status.username}</strong>
                </Alert>
              )}

              <Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  1. Cuenta SMTP (envío)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Credenciales con las que el sistema se autentica. No requiere confirmación por código:
                  al cargar la contraseña ya se demuestra acceso.
                </Typography>

                <Stack spacing={2.5} sx={{ maxWidth: 520 }}>
                  <TextField
                    label="Correo de la cuenta SMTP"
                    type="email"
                    value={form.email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    fullWidth
                    required
                    placeholder="empresa@miempresa.com"
                    helperText="Según el dominio (gmail, outlook u otro) cambiamos las instrucciones de abajo."
                    disabled={busy}
                  />

                  {form.email.includes('@') && (
                    <Box>
                      {isKnownProvider ? (
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`Proveedor detectado: ${SMTP_PRESETS[detectedProvider].label}`}
                        />
                      ) : (
                        <Chip
                          size="small"
                          variant="outlined"
                          label="Otro dominio / host — revise SMTP e instrucciones"
                        />
                      )}
                    </Box>
                  )}

                  <TextField
                    label="Contraseña de aplicación"
                    type="password"
                    value={form.appPassword}
                    onChange={(e) => setForm((prev) => ({ ...prev, appPassword: e.target.value }))}
                    fullWidth
                    required={!status?.tieneContrasena}
                    placeholder="xxxx xxxx xxxx xxxx"
                    helperText={
                      status?.contrasenaLegible === false
                        ? 'La contraseña guardada no se puede leer. Ingresala de nuevo para reactivar el envío.'
                        : status?.tieneContrasena
                          ? 'Deje en blanco para conservar la contraseña actual.'
                          : 'Usá la contraseña de aplicación del proveedor (ver instrucciones).'
                    }
                    disabled={busy}
                  />

                  <TextField
                    label="Nombre por defecto (opcional)"
                    value={form.fromName}
                    onChange={(e) => setForm((prev) => ({ ...prev, fromName: e.target.value }))}
                    fullWidth
                    placeholder="Mi Empresa S.A."
                    disabled={busy}
                  />

                  {form.email.includes('@') && (
                    <Accordion variant="outlined" disableGutters defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">{helpTitle}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {smtpHelpForProvider(detectedProvider)}
                      </AccordionDetails>
                    </Accordion>
                  )}

                  {(!isKnownProvider && form.email.includes('@')) && (
                    <Accordion variant="outlined" disableGutters defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="body2">Servidor SMTP</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Stack spacing={2}>
                          <TextField
                            label="Servidor SMTP"
                            value={form.smtpServer}
                            onChange={(e) => setForm((prev) => ({ ...prev, smtpServer: e.target.value }))}
                            fullWidth
                            required
                            placeholder="smtp.miempresa.com"
                            disabled={busy}
                          />
                          <TextField
                            label="Puerto"
                            type="number"
                            value={form.smtpPort}
                            onChange={(e) =>
                              setForm((prev) => ({
                                ...prev,
                                smtpPort: Number(e.target.value) || 587,
                              }))
                            }
                            fullWidth
                            required
                            disabled={busy}
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                checked={form.enableSsl}
                                onChange={(e) =>
                                  setForm((prev) => ({ ...prev, enableSsl: e.target.checked }))
                                }
                                disabled={busy}
                              />
                            }
                            label="Usar SSL/TLS"
                          />
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pt: 1 }}>
                    <Button variant="contained" onClick={handleSaveSmtp} disabled={busy}>
                      {savingSmtp ? 'Guardando…' : status?.configurado ? 'Actualizar SMTP' : 'Guardar SMTP'}
                    </Button>
                    {status?.configurado && (
                      <Button variant="outlined" color="error" onClick={handleRemoveAll} disabled={busy}>
                        Quitar todo
                      </Button>
                    )}
                  </Stack>

                  {status?.configurado && (
                    <Box sx={{ mt: 1, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                        Probar configuración
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                        Envía un correo de prueba con el SMTP guardado para verificar servidor, puerto y credenciales.
                      </Typography>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                        <TextField
                          label="Enviar prueba a"
                          type="email"
                          size="small"
                          value={testEmail}
                          onChange={(e) => setTestEmail(e.target.value)}
                          fullWidth
                          placeholder={form.email || 'destino@empresa.com'}
                          disabled={busy}
                        />
                        <Button
                          variant="outlined"
                          onClick={handleTestSmtp}
                          disabled={busy}
                          sx={{ whiteSpace: 'nowrap', minWidth: 140 }}
                        >
                          {testingSmtp ? 'Enviando…' : 'Probar envío'}
                        </Button>
                      </Stack>
                    </Box>
                  )}
                </Stack>
              </Box>

              <Divider />

              <Box>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                  2. Remitentes por tipo de documento
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Si el correo es distinto al SMTP, eOne envía un código a esa casilla para confirmarlo.
                  Si coincide con la cuenta SMTP, se activa sin código.
                </Typography>

                {!status?.configurado && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Primero guarde la cuenta SMTP. Luego podrá agregar remitentes por tipo.
                  </Alert>
                )}

                {status?.configurado && !isKnownProvider && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Su servidor SMTP no es Gmail ni Outlook. La mayoría de los hostings
                    (Hostinger, cPanel, etc.) solo permiten enviar desde la misma casilla
                    autenticada{status?.username ? ` (${status.username})` : ''}. Si usa un
                    correo de remitente distinto, es probable que el servidor lo rechace.
                    Recomendamos usar la misma cuenta del SMTP como remitente.
                  </Alert>
                )}

                {remitentes.length > 0 && (
                  <Table size="small" sx={{ mb: 2, maxWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Correo visible</TableCell>
                        <TableCell>Estado</TableCell>
                        <TableCell align="right">Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {remitentes.map((r) => (
                        <TableRow key={r.tipoDocumento} hover>
                          <TableCell>{labelCorreoTipoDocumento(r.tipoDocumento)}</TableCell>
                          <TableCell>
                            <Typography variant="body2">{r.fromEmail}</Typography>
                            {r.fromName ? (
                              <Typography variant="caption" color="text.secondary">{r.fromName}</Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {r.confirmado ? (
                              <Chip size="small" color="success" label="Confirmado" />
                            ) : (
                              <Chip size="small" color="warning" label="Pendiente de código" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              onClick={() => handleEditRemitente(r)}
                              disabled={busy || !status?.configurado}
                            >
                              Editar
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteOutlineIcon />}
                              onClick={() => void handleRemoveRemitente(r.tipoDocumento)}
                              disabled={busy || !status?.configurado}
                            >
                              Quitar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}

                {remitentes.some((r) => r.pendienteConfirmacion) && (
                  <Stack spacing={2} sx={{ mb: 3, maxWidth: 520 }}>
                    <Alert severity="warning">
                      Ingresá el código que llegó al correo del remitente (enviado desde eOne).
                    </Alert>
                    {remitentes
                      .filter((r) => r.pendienteConfirmacion)
                      .map((r) => (
                        <Stack
                          key={`confirm-${r.tipoDocumento}`}
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={1}
                          alignItems={{ sm: 'center' }}
                        >
                          <Typography variant="body2" sx={{ minWidth: 140 }}>
                            {labelCorreoTipoDocumento(r.tipoDocumento)}
                          </Typography>
                          <TextField
                            size="small"
                            label="Código"
                            value={codigos[r.tipoDocumento] ?? ''}
                            onChange={(e) =>
                              setCodigos((prev) => ({ ...prev, [r.tipoDocumento]: e.target.value }))
                            }
                            disabled={busy}
                            sx={{ width: 140 }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => void handleConfirm(r.tipoDocumento)}
                            disabled={busy}
                          >
                            {confirmingTipo === r.tipoDocumento ? '…' : 'Confirmar'}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => void handleResend(r.tipoDocumento)}
                            disabled={busy}
                          >
                            Reenviar código
                          </Button>
                        </Stack>
                      ))}
                  </Stack>
                )}

                <Stack spacing={2} sx={{ maxWidth: 520 }}>
                  <FormControl fullWidth disabled={busy || !status?.configurado}>
                    <InputLabel id="tipo-doc-label">Tipo de documento</InputLabel>
                    <Select
                      labelId="tipo-doc-label"
                      label="Tipo de documento"
                      value={remitenteForm.tipoDocumento}
                      onChange={(e) =>
                        setRemitenteForm((prev) => ({ ...prev, tipoDocumento: String(e.target.value) }))
                      }
                    >
                      {CORREO_TIPOS_DOCUMENTO.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                          {t.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    label="Correo del remitente"
                    type="email"
                    value={remitenteForm.fromEmail}
                    onChange={(e) =>
                      setRemitenteForm((prev) => ({ ...prev, fromEmail: e.target.value }))
                    }
                    fullWidth
                    required
                    placeholder="facturacion@miempresa.com"
                    disabled={busy || !status?.configurado}
                  />

                  <TextField
                    label="Nombre visible (opcional)"
                    value={remitenteForm.fromName}
                    onChange={(e) =>
                      setRemitenteForm((prev) => ({ ...prev, fromName: e.target.value }))
                    }
                    fullWidth
                    placeholder="Facturación - Mi Empresa"
                    disabled={busy || !status?.configurado}
                  />

                  <Button
                    variant="contained"
                    onClick={handleSaveRemitente}
                    disabled={busy || !status?.configurado}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {savingRemitente ? 'Guardando…' : 'Guardar remitente'}
                  </Button>
                </Stack>
              </Box>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CorreoSalida;
